import * as React from 'react';
import { useMemo } from 'react';
import { bulkDeleteUnits, deleteUnit } from '@/api/units';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  InputAdornment,
  lighten,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Columns as ColumnsIcon,
  FileCsv as CsvIcon,
  DownloadSimple as DownloadIcon,
  FileXls as ExcelIcon,
  Funnel as FunnelIcon,
  HouseLine as HouseLineIcon,
  Plus as PlusIcon,
  MagnifyingGlass as SearchIcon,
  Trash as TrashIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { RouterLink } from '@/components/core/v2/link';

import { CreateOrEditUnitModal } from './create-or-edit-unit-modal';

interface PaginationMeta {
  per_page: number;
  current_page: number;
  has_more: boolean;
  next_page: string | null;
  prev_page: string | null;
  total: number;
  total_pages: number;
}

interface UnitsTableProps {
  rows: Unit[];
  loading: boolean;
  onSuccess: (unit: Unit) => void;
  onDeleteSuccess: (id: string) => void;
  onBulkDeleteSuccess?: () => void;
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

// Helper function to check if user has required roles for bulk delete
const hasBulkDeletePermission = (user: any): boolean => {
  if (!user) return false;

  // Check account roles
  const accountRoleNames = user.account_roles?.map((role: any) => role.name) || [];
  const hasAccountPermission = accountRoleNames.some((roleName: string) =>
    ['account_admin', 'account_manager', 'account_owner'].includes(roleName)
  );

  // Check community roles
  const communityRoleNames = user.community_roles?.map((role: any) => role.name) || [];
  const hasCommunityPermission = communityRoleNames.includes('community_manager');

  return hasAccountPermission || hasCommunityPermission;
};

export function UnitsTable({
  rows,
  loading,
  onSuccess,
  onDeleteSuccess,
  onBulkDeleteSuccess,
  pagination,
  onPageChange,
  onRowsPerPageChange,
}: UnitsTableProps): React.JSX.Element {
  const dialog = useDialog();
  const confirm = useConfirm();
  const theme = useTheme();
  const { community } = useUser();
  const navigate = useNavigate();
  const [columnVisibilityAnchor, setColumnVisibilityAnchor] = React.useState<null | HTMLElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(new Set());

  const columns = useMemo<MRT_ColumnDef<Unit>[]>(
    () => [
      {
        accessorKey: 'unit_number',
        header: 'Unit Number',
        size: 150,
        accessorFn: (row) => row.unit_number || 'NA',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search unit numbers...',
        Cell: ({ row }) => (
          <Typography color={row.original.unit_number ? 'text.primary' : 'text.secondary'} variant="body2">
            {row.original.unit_number || 'No number'}
          </Typography>
        ),
      },
      {
        accessorKey: 'address',
        header: 'Address',
        size: 400,
        accessorFn: (row) => row.address || 'N/A',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search addresses...',
        Cell: ({ row }) => (
          <Link
            component={RouterLink}
            href={paths.units.details(row.original.id?.toString() ?? '')}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {row.original.address || 'N/A'}
          </Link>
        ),
      },
      {
        accessorKey: 'users',
        header: 'Residents',
        size: 250,
        accessorFn: (row) => {
          const users =
            row.current_residents && row.current_residents.length > 0 ? row.current_residents : (row.owners ?? []);
          if (!users || users.length === 0) {
            return 'None';
          }
          return users
            .map((u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || 'User')
            .join(', ');
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search residents...',
        Cell: ({ row }) => {
          const users =
            row.original.current_residents && row.original.current_residents.length > 0
              ? row.original.current_residents
              : (row.original.owners ?? []);
          if (!users || users.length === 0) {
            return (
              <Chip color="error" icon={<HouseLineIcon size={16} />} label="Vacant" size="small" variant="outlined" />
            );
          }
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} spacing={0.5}>
              <Chip
                color="primary"
                icon={<HouseLineIcon size={16} />}
                label={`${users.length} resident${users.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
              <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                {users.slice(0, 3).map((u: any) => {
                  const label = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || 'User';
                  const initials = (u.first_name?.[0] ?? '') + (u.last_name?.[0] ?? '');
                  return (
                    <Tooltip key={u.id} title={label}>
                      <Avatar alt={label} src={u.avatar ?? undefined} sx={{ width: 24, height: 24, fontSize: 10 }}>
                        {!u.avatar && initials ? initials.toUpperCase() : null}
                      </Avatar>
                    </Tooltip>
                  );
                })}
              </AvatarGroup>
            </Stack>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 200,
        accessorFn: (row) => dayjs(row.created_at).format('MMM D, YYYY'),
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search dates...',
        Cell: ({ row }) => (
          <Typography color="text.secondary" variant="body2">
            {dayjs(row.original.created_at).format('MMM D, YYYY')}
          </Typography>
        ),
      },
    ],
    []
  );

  const handleDeleteUnit = useMutation({
    mutationFn: (id: string) => deleteUnit(id),
  });

  const handleBulkDeleteUnits = useMutation({
    mutationFn: (unitIds: string[]) => bulkDeleteUnits(unitIds),
  });

  const handleSuccess = (unit: Unit) => {
    onSuccess(unit);
    dialog.handleClose();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  // Export functions
  const exportToCsv = () => {
    const visibleColumns = columns.filter((col) => !hiddenColumns.has(col.accessorKey as string));
    const headers = visibleColumns.map((col) => col.header).join(',');

    const csvData = rows
      .map((row) => {
        return visibleColumns
          .map((col) => {
            if (col.accessorKey === 'unit_number') {
              return row.unit_number || 'NA';
            }
            if (col.accessorKey === 'address') {
              return `"${row.address || 'N/A'}"`;
            }
            if (col.accessorKey === 'users') {
              const users =
                row.current_residents && row.current_residents.length > 0 ? row.current_residents : (row.owners ?? []);
              if (!users || users.length === 0) return 'None';
              return `"${users.map((u: any) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || 'User').join(', ')}"`;
            }
            if (col.accessorKey === 'created_at') {
              return dayjs(row.created_at).format('MMM D, YYYY');
            }
            return row[col.accessorKey as keyof Unit] || '';
          })
          .join(',');
      })
      .join('\n');

    const csvContent = `${headers}\n${csvData}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `community-units-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    // For now, we'll use CSV format but with .xls extension
    // In a real app, you'd use a library like xlsx to create actual Excel files
    exportToCsv();
  };

  const toggleColumnVisibility = (columnKey: string) => {
    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(columnKey)) {
      newHiddenColumns.delete(columnKey);
    } else {
      newHiddenColumns.add(columnKey);
    }
    setHiddenColumns(newHiddenColumns);
  };

  const handleDelete = React.useCallback((id: string) => {
    confirm({
      title: 'Delete Unit',
      description: 'Are you sure you want to delete this unit?',
      confirmationText: 'Rescind',
      cancellationText: 'Cancel',
    })
      .then(async () => {
        await handleDeleteUnit.mutateAsync(id);
        onDeleteSuccess(id);
        toast.success('Unit deleted', {
          style: {
            backgroundColor: theme.palette.success.dark,
            color: theme.palette.success.contrastText,
          },
          position: 'top-right',
        });
      })
      .catch(() => {
        toast.error('Unit not deleted', {
          position: 'top-right',
        });
      });
  }, []);

  const table = useMaterialReactTable<Unit>({
    columns: columns.filter((col) => !hiddenColumns.has(col.accessorKey as string)),
    data: rows,
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: false,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      sorting: [{ id: 'address', desc: false }],
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Search all units...',
      InputProps: {
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon size={20} />
          </InputAdornment>
        ),
      },
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: false,
    enablePagination: true,
    enableColumnFilters: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enableSorting: true,
    enableColumnResizing: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSelectAll: true,

    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: 'background.paper',
        fontWeight: 600,
        fontSize: '0.875rem',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      },
    },
    muiTableBodyRowProps: ({ row }: { row: any }) => ({
      onClick: () => { navigate(paths.units.details(row.original.id.toString())); },
      sx: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      },
    }),
    muiTableContainerProps: (props) => ({
      ...props,
      sx: {
        minHeight: '500px',
        '& .MuiTable-root': {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
        '& .MuiTableHead-root .MuiTableRow-root': {
          backgroundColor: 'background.paper',
          fontWeight: 600,
          '& .MuiTableCell-root': {
            borderBottom: '2px solid',
            borderColor: 'divider',
            fontWeight: 600,
          },
        },
        '& .MuiTableBody-root .MuiTableRow-root:hover': {
          backgroundColor: 'action.hover',
        },
      },
    }),

    renderTopToolbar: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().rows;
      const hasSelection = selectedRows.length > 0;
      const canBulkDelete = hasSelection && hasBulkDeletePermission(community);

      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '1rem',
            p: '16px',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          })}
        >
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FunnelIcon color="var(--mui-palette-primary-main)" size={20} />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle2">
                Filters & Search
              </Typography>
            </Box>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />

            {/* Bulk Delete Button */}
            {hasSelection ? <RoleGuard
                requiredAccountRoles={[
                  ACCOUNT_ROLES.ACCOUNT_ADMIN,
                  ACCOUNT_ROLES.ACCOUNT_MANAGER,
                  ACCOUNT_ROLES.ACCOUNT_OWNER,
                ]}
                requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
              >
                <Button
                  color="error"
                  disabled={handleBulkDeleteUnits.isPending}
                  onClick={handleBulkDelete}
                  size="small"
                  startIcon={<TrashIcon size={18} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    ml: 2,
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    },
                  }}
                  variant="contained"
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              </RoleGuard> : null}
          </Box>

          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {/* Column Visibility Button */}
            <Button
              onClick={(e) => { setColumnVisibilityAnchor(e.currentTarget); }}
              size="small"
              startIcon={<ColumnsIcon size={18} />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
              variant="outlined"
            >
              Columns
            </Button>

            {/* Export Button */}
            <Button
              onClick={(e) => { setExportMenuAnchor(e.currentTarget); }}
              size="small"
              startIcon={<DownloadIcon size={18} />}
              sx={{ textTransform: 'none', fontWeight: 500 }}
              variant="outlined"
            >
              Export
            </Button>
          </Box>
        </Box>
      );
    },
  });

  const handleBulkDelete = React.useCallback(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedUnitIds = selectedRows.map((row) => row.original.id?.toString() ?? '');

    if (selectedUnitIds.length === 0) {
      toast.error('No units selected', {
        position: 'top-right',
      });
      return;
    }

    confirm({
      title: 'Delete Selected Units',
      description: `Are you sure you want to delete ${selectedUnitIds.length} selected unit(s)? This action cannot be undone.`,
      confirmationText: 'Delete All',
      cancellationText: 'Cancel',
    })
      .then(async () => {
        try {
          const result = await handleBulkDeleteUnits.mutateAsync(selectedUnitIds);

          if (result.success) {
            // Clear selection
            table.resetRowSelection();

            // Trigger data refresh in parent component
            if (onBulkDeleteSuccess) {
              onBulkDeleteSuccess();
            }

            // For bulk operations, don't call individual onDeleteSuccess callbacks
            // The parent component should handle refreshing the data

            // Create a consolidated message
            let message = result.message;
            if (result.details.errors.length > 0) {
              message += ` (${result.details.errors.length} error(s) occurred)`;
            }

            // Show success message with error count if any
            toast.success(message, {
              icon: result.details.errors.length > 0 ? <WarningIcon /> : <CheckCircleIcon />,
              style: {
                backgroundColor: result.details.errors.length > 0
                  ? theme.palette.warning.dark
                  : theme.palette.success.dark,
                color: result.details.errors.length > 0
                  ? theme.palette.warning.contrastText
                  : theme.palette.success.contrastText,
              },
              position: 'top-right',
              duration: 5000, // Show longer for partial failures
            });

            // If there are errors, show a detailed error message
            if (result.details.errors.length > 0) {
              setTimeout(() => {
                toast.error(`Errors: ${result.details.errors.join(', ')}`, {
                  position: 'top-right',
                  duration: 7000,
                });
              }, 1000);
            }
          } else {
            toast.error(result.message, {
              position: 'top-right',
            });
          }
        } catch (error) {
          toast.error('Failed to delete selected units', {
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        // User cancelled
      });
  }, [handleBulkDeleteUnits, onDeleteSuccess, table, theme]);

  return (
    <React.Fragment>
      <Card
        sx={{
          mt: 4,
        }}
      >
        <Box 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Chip 
              color="primary" 
              label={`${rows.length} Total Units`} 
              size="medium" 
              variant="outlined" 
            />
            <Chip
              color="success"
              label={`${rows.filter((u) => u.current_residents && u.current_residents.length > 0).length} Occupied`}
              size="medium"
              variant="outlined"
            />
            <Chip
              color="info"
              label={`${rows.filter((u) => !u.current_residents || u.current_residents.length === 0).length} Vacant`}
              size="medium"
              variant="outlined"
            />
          </Stack>
          
          <Button
            onClick={() => {
              dialog.handleOpen();
            }}
            size="small"
            startIcon={<PlusIcon size={18} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            variant="contained"
          >
            Add Unit
          </Button>
        </Box>
        <MaterialReactTable table={table} />
      </Card>

      {/* Column Visibility Menu */}
      <Menu
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            overflow: 'auto',
          },
        }}
        anchorEl={columnVisibilityAnchor}
        onClose={() => { setColumnVisibilityAnchor(null); }}
        open={Boolean(columnVisibilityAnchor)}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle2">
            Column Visibility
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Select which columns to display
          </Typography>
        </Box>
        {columns.map((column) => (
          <MenuItem key={column.accessorKey} onClick={() => { toggleColumnVisibility(column.accessorKey as string); }}>
            <ListItemIcon>
              <Checkbox checked={!hiddenColumns.has(column.accessorKey as string)} size="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">{column.header}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Export Menu */}
      <Menu
        PaperProps={{
          sx: {
            minWidth: 200,
          },
        }}
        anchorEl={exportMenuAnchor}
        onClose={() => { setExportMenuAnchor(null); }}
        open={Boolean(exportMenuAnchor)}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle2">
            Export Data
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Choose export format
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            exportToCsv();
            setExportMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <CsvIcon size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Export to CSV</Typography>
            <Typography color="text.secondary" variant="caption">
              Download as .csv file
            </Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            exportToExcel();
            setExportMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <ExcelIcon size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Export to Excel</Typography>
            <Typography color="text.secondary" variant="caption">
              Download as .xls file
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
      <CreateOrEditUnitModal
        communityId={community?.id}
        communityUnitTypes={community?.unit_types}
        mode="create"
        onClose={dialog.handleClose}
        onSuccess={handleSuccess}
        open={dialog.open}
      />
    </React.Fragment>
  );
}
